import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const { FiLayers, FiPlus, FiTrash2, FiEdit3, FiCheck, FiX, FiInfo } = FiIcons;

const GroupConfig = () => {
  const [groups, setGroups] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: '', description: '', color_code: '#6366f1' });

  useEffect(() => { fetchGroups(); }, []);

  const fetchGroups = async () => {
    const { data } = await supabase.from('user_groups_config_1740000000004').select('*').order('name');
    if (data) setGroups(data);
  };

  const handleCreate = async () => {
    if (!newGroup.name) return;
    const { error } = await supabase.from('user_groups_config_1740000000004').insert([newGroup]);
    if (!error) {
      fetchGroups();
      setIsAdding(false);
      setNewGroup({ name: '', description: '', color_code: '#6366f1' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deleting this group will re-categorize associated users. Proceed?")) return;
    await supabase.from('user_groups_config_1740000000004').delete().eq('id', id);
    fetchGroups();
  };

  return (
    <div className="space-y-10">
      <div className="bg-white p-10 rounded-[2.5rem] border border-slate-100 shadow-2xl shadow-indigo-900/5 flex flex-col md:flex-row justify-between items-center gap-8">
        <div>
          <h2 className="text-3xl font-black text-slate-900 tracking-tight flex items-center gap-4">
            <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center text-white shadow-xl shadow-indigo-200">
              <SafeIcon icon={FiLayers} />
            </div>
            Exclusion Groups
          </h2>
          <p className="text-slate-400 font-bold uppercase text-[10px] tracking-[0.2em] mt-2">Manage arbitrary identification categories</p>
        </div>
        <button 
          onClick={() => setIsAdding(true)}
          className="px-8 py-4 bg-slate-900 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] flex items-center gap-2 hover:bg-slate-800 transition-all shadow-xl"
        >
          <SafeIcon icon={FiPlus} /> Define New Group
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <AnimatePresence>
          {isAdding && (
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-indigo-600 p-8 rounded-[2.5rem] shadow-2xl text-white">
              <h3 className="text-xl font-black mb-6">New Group Definition</h3>
              <div className="space-y-4">
                <input 
                  className="w-full bg-white/10 border-white/20 rounded-xl px-4 py-3 text-sm font-bold placeholder:text-indigo-200 outline-none focus:bg-white/20 transition-all"
                  placeholder="Group Name (e.g. Partners)"
                  value={newGroup.name}
                  onChange={e => setNewGroup({...newGroup, name: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
                />
                <textarea 
                  className="w-full bg-white/10 border-white/20 rounded-xl px-4 py-3 text-sm font-bold placeholder:text-indigo-200 outline-none focus:bg-white/20 h-24 resize-none"
                  placeholder="Purpose of this group..."
                  value={newGroup.description}
                  onChange={e => setNewGroup({...newGroup, description: e.target.value})}
                />
                <div className="flex justify-between items-center pt-4">
                  <button onClick={() => setIsAdding(false)} className="text-indigo-200 font-black uppercase text-[10px] tracking-widest">Cancel</button>
                  <button onClick={handleCreate} className="bg-white text-indigo-600 px-6 py-3 rounded-xl font-black uppercase text-[10px] tracking-widest shadow-lg">Save Group</button>
                </div>
              </div>
            </motion.div>
          )}

          {groups.map((group) => (
            <motion.div 
              key={group.id} 
              layout
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm hover:shadow-xl transition-all relative group overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-2 h-full" style={{ backgroundColor: group.color_code }}></div>
              <div className="flex justify-between items-start mb-6">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white shadow-lg" style={{ backgroundColor: group.color_code }}>
                  <SafeIcon icon={FiLayers} />
                </div>
                <button 
                  onClick={() => handleDelete(group.id)}
                  className="opacity-0 group-hover:opacity-100 p-2 text-slate-300 hover:text-red-500 transition-all"
                >
                  <SafeIcon icon={FiTrash2} />
                </button>
              </div>
              <h4 className="text-lg font-black text-slate-900 mb-2 truncate">{group.name}</h4>
              <p className="text-xs text-slate-400 font-bold leading-relaxed mb-6 h-12 overflow-hidden line-clamp-3">
                {group.description || 'No description provided for this group.'}
              </p>
              <div className="flex items-center gap-2 text-[10px] font-black text-slate-300 uppercase tracking-widest italic pt-4 border-t border-slate-50">
                <SafeIcon icon={FiInfo} /> Maintainable Policy
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default GroupConfig;